/**
 * Whether a model may take a given budgeted upgrade (Gifts of Chaos, Chaos
 * Mutations, Forest Spites, Vampiric Powers, Daemonic Gifts, Incantation
 * Scrolls).
 *
 * Eligibility is read from the structured `restriction` tags stamped onto the
 * data by db/scripts/upgrades/restriction-tags.ts, never from the prose. An
 * upgrade with no tags is unrestricted, so data that has not been tagged stays
 * available rather than silently disappearing from the picker.
 */
import type { Faction, KnightlyVirtue, Unit, UpgradeRestriction } from '../types/faction';
import type { ArmyEntry } from '../types/army';

const GODS = ['khorne', 'nurgle', 'slaanesh', 'tzeentch'] as const;

/**
 * The Wizard level the model actually fights at: its printed level, raised by
 * any wizard upgrade it has bought ("Level 2 Wizard", "Be a Level 3 Wizard")
 * and, when the faction is supplied, by budgeted upgrades that increase it
 * (Master of the Black Arts: "increases their Level of Wizardry by 1").
 */
export function effectiveWizardLevel(unit: Unit, entry: ArmyEntry, faction?: Faction): number {
  let level = unit.magic?.wizard_level ?? 0;
  for (const desc of entry.selectedOptions) {
    const m = /level (\d)\s+wizard/i.exec(desc);
    if (m) level = Math.max(level, Number(m[1]));
  }
  if (faction && level > 0) {
    for (const id of new Set(entry.selectedUpgradeIds ?? [])) {
      const up = findUpgrade(faction, id);
      const m = /increases? their level of wizardry by (\d)/i.exec(up?.description ?? '');
      if (m) level += Number(m[1]);
    }
  }
  return level;
}

/**
 * Which Chaos god the model is aligned to. Daemons carry it intrinsically as a
 * `daemon_of_*` special rule; a Daemon Prince picks one as a Daemonic Alignment
 * option instead.
 */
export function alignedGods(unit: Unit, entry: ArmyEntry): string[] {
  const found = new Set<string>();
  for (const rule of unit.special_rules ?? []) {
    const m = /^daemon_of_(\w+)$/.exec(rule);
    if (m) found.add(m[1].toLowerCase());
  }
  for (const desc of entry.selectedOptions) {
    for (const god of GODS) {
      if (new RegExp(`\\b(daemon|mark) of ${god}\\b`, 'i').test(desc)) found.add(god);
    }
  }
  return [...found];
}

/** Troop type with the parenthetical qualifier stripped: "Heavy infantry (character)" -> "heavy infantry" */
function troopType(unit: Unit): string {
  return (unit.troop_type ?? '').replace(/\([^)]*\)/g, '').trim().toLowerCase();
}

/**
 * A required troop type matches either exactly ('regular infantry') or as the
 * broad category ('infantry' covers regular, heavy and monstrous infantry).
 */
function troopTypeMatches(unit: Unit, wanted: string[]): boolean {
  const actual = troopType(unit);
  return wanted.some((w) => actual === w || actual.endsWith(` ${w}`) || actual === w);
}

export interface UpgradeEligibility {
  eligible: boolean;
  /** Why not, phrased for display next to the upgrade */
  reason?: string;
}

/**
 * Model-level eligibility. Army-wide rules (once per army) are not checked here
 * because they depend on the rest of the list — validateArmy reports those.
 */
export function upgradeEligibility(
  upgrade: KnightlyVirtue,
  unit: Unit,
  entry: ArmyEntry,
  faction?: Faction,
): UpgradeEligibility {
  const r: UpgradeRestriction | undefined = upgrade.restriction;
  if (!r) return { eligible: true };

  if (r.requires_character && unit.category !== 'character') {
    return { eligible: false, reason: 'Characters only' };
  }

  if (r.unit_names?.length) {
    const name = unit.name.toLowerCase();
    if (!r.unit_names.some((n) => name === n)) {
      return { eligible: false, reason: `${r.unit_names.map(titleCase).join(' or ')} only` };
    }
  }

  if (r.unit_name_contains && !unit.name.toLowerCase().includes(r.unit_name_contains)) {
    return { eligible: false, reason: `${titleCase(r.unit_name_contains)}s only` };
  }

  if (r.troop_types?.length && !troopTypeMatches(unit, r.troop_types)) {
    return { eligible: false, reason: `${r.troop_types.join(' or ')} only` };
  }

  if (r.gods?.length) {
    const mine = alignedGods(unit, entry);
    if (!r.gods.some((g) => mine.includes(g))) {
      const needed = r.gods.map(titleCase).join(' or ');
      return {
        eligible: false,
        reason: mine.length === 0
          ? `Daemons of ${needed} only - choose a Daemonic Alignment first`
          : `Daemons of ${needed} only`,
      };
    }
  }

  const level = effectiveWizardLevel(unit, entry, faction);

  if (r.requires_wizard && level < 1) {
    return { eligible: false, reason: 'Wizards only' };
  }

  if (r.min_wizard_level !== undefined && level < r.min_wizard_level) {
    return { eligible: false, reason: `Level ${r.min_wizard_level} Wizards only` };
  }

  if (r.unit_wizard_levels?.length) {
    const name = unit.name.toLowerCase();
    const clause = r.unit_wizard_levels.find((c) => c.unit_name === name);
    if (!clause) {
      const who = r.unit_wizard_levels.map((c) => titleCase(c.unit_name)).join(' or ');
      return { eligible: false, reason: `${who} only` };
    }
    if (level < clause.level) {
      return { eligible: false, reason: `${unit.name} must be a Level ${clause.level} Wizard` };
    }
  }

  return { eligible: true };
}

/** How many copies of an upgrade a single model may take */
export function maxPerModel(upgrade: KnightlyVirtue): number {
  return upgrade.restriction?.max_per_model ?? 1;
}

/** Whether an upgrade may appear on only one model in the whole army */
export function isOncePerArmy(upgrade: KnightlyVirtue): boolean {
  return upgrade.restriction?.once_per_army === true;
}

/** Every budgeted upgrade in the faction, across all families */
export function findUpgrade(faction: Faction, id: string): KnightlyVirtue | undefined {
  const families: (KnightlyVirtue[] | undefined)[] = [
    faction.gifts_of_chaos, faction.chaos_mutations, faction.forest_sprites,
    faction.vampiric_powers, faction.chaotic_traits, faction.daemonic_gifts,
    faction.incantation_scrolls, faction.disciplines_of_the_old_ones,
  ];
  for (const family of families) {
    const hit = family?.find((u) => u.id === id);
    if (hit) return hit;
  }
  return undefined;
}

function titleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}
